import React, { useEffect, useState } from "react";
import { Alert, Button, Card, List, Modal, Space, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import useApiCall from "../common/api-call";

const parseCompositeString = raw => {
  let value = raw;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string") {
      value = parsed;
    } else {
      return JSON.stringify(parsed, null, 2);
    }
  } catch (e) {
    void e;
  }
  return value
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\\t/g, "\t");
};

const JsonNode = ({ value, onStringClick, path }) => {
  if (value === null) {
    return <span style={{ color: "#777" }}>null</span>;
  }

  if (Array.isArray(value)) {
    return (
      <div style={{ paddingLeft: 16 }}>
        {value.map((item, index) => (
          <div key={`${path}[${index}]`} style={{ marginBottom: 4 }}>
            <Typography.Text type="secondary">[{index}]</Typography.Text>:{" "}
            <JsonNode
              value={item}
              onStringClick={onStringClick}
              path={`${path}[${index}]`}
            />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === "object") {
    return (
      <div style={{ paddingLeft: 16 }}>
        {Object.entries(value).map(([key, itemValue]) => (
          <div key={`${path}.${key}`} style={{ marginBottom: 4 }}>
            <Typography.Text strong>{key}</Typography.Text>:{" "}
            <JsonNode
              value={itemValue}
              onStringClick={onStringClick}
              path={`${path}.${key}`}
            />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === "string") {
    return (
      <Button
        type="link"
        size="small"
        style={{ padding: 0, height: "auto" }}
        onClick={() => onStringClick(value, path)}
      >
        {value.length > 140 ? `"${value.slice(0, 140)}..."` : `"${value}"`}
      </Button>
    );
  }

  return <span>{String(value)}</span>;
};

const DebugAgent = () => {
  const [listLoading, setListLoading] = useState(false);
  const [replayLoading, setReplayLoading] = useState(false);
  const [deletingRecordId, setDeletingRecordId] = useState("");
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stringDialogOpen, setStringDialogOpen] = useState(false);
  const [selectedStringPath, setSelectedStringPath] = useState("");
  const [parsedStringContent, setParsedStringContent] = useState("");
  const { apiCall } = useApiCall();

  const loadRecords = async () => {
    setListLoading(true);
    setError(null);
    try {
      const response = await apiCall("/debug/record/list", "POST", { limit: 10 }, "admin");
      if (!response || !Array.isArray(response.records)) {
        setError("Failed to load records.");
        setRecords([]);
        setSelectedRecord(null);
        return;
      }
      setRecords(response.records);
      if (response.records.length === 0) {
        setSelectedRecord(null);
        return;
      }
      setSelectedRecord(prev => {
        if (!prev) {
          return response.records[0];
        }
        const matched = response.records.find(item => item.recordId === prev.recordId);
        return matched || response.records[0];
      });
    } catch (e) {
      setError("Failed to load records.");
      setRecords([]);
      setSelectedRecord(null);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const handleReplay = async () => {
    if (!selectedRecord || !selectedRecord.recordId || replayLoading) {
      return;
    }
    setReplayLoading(true);
    setResult(null);
    setError(null);
    try {
      const response = await apiCall(
        "/debug/replay",
        "POST",
        { recordId: selectedRecord.recordId },
        "admin",
      );
      if (!response) {
        setError("Replay request failed.");
        return;
      }
      setResult(response);
    } catch (e) {
      setError("Replay request failed.");
    } finally {
      setReplayLoading(false);
    }
  };

  const handleDeleteRecord = async (e, recordId) => {
    e.stopPropagation();
    if (!recordId || deletingRecordId) {
      return;
    }
    setDeletingRecordId(recordId);
    setError(null);
    setResult(null);
    try {
      const response = await apiCall(
        "/debug/record/delete",
        "POST",
        { recordId },
        "admin",
      );
      if (!response) {
        setError("Delete request failed.");
        return;
      }
      await loadRecords();
    } catch (e2) {
      setError("Delete request failed.");
    } finally {
      setDeletingRecordId("");
    }
  };

  const handleStringClick = (value, path) => {
    setSelectedStringPath(path);
    setParsedStringContent(parseCompositeString(value));
    setStringDialogOpen(true);
  };

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ marginBottom: 8 }}>
          Agent Replay Debug
        </Typography.Title>
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Load latest 10 records, select one, inspect path and payload, then replay.
        </Typography.Text>
        <Space style={{ marginBottom: 16 }}>
          <Button onClick={loadRecords} loading={listLoading}>
            Refresh
          </Button>
          <Button
            type="primary"
            onClick={handleReplay}
            disabled={!selectedRecord || replayLoading}
            loading={replayLoading}
          >
            Replay
          </Button>
        </Space>
        <List
          bordered
          loading={listLoading}
          dataSource={records}
          locale={{ emptyText: "No records" }}
          renderItem={item => (
            <List.Item
              actions={[
                <Button
                  key="delete"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingRecordId === item.recordId}
                  onClick={e => handleDeleteRecord(e, item.recordId)}
                />,
              ]}
              onClick={() => setSelectedRecord(item)}
              style={{
                cursor: "pointer",
                backgroundColor:
                  selectedRecord && selectedRecord.recordId === item.recordId
                    ? "#e6f4ff"
                    : "transparent",
              }}
            >
              <div style={{ width: "100%" }}>
                <Typography.Text strong>{item.recordId}</Typography.Text>
                <div style={{ marginTop: 4 }}>
                  <Typography.Text type="secondary">{item.path}</Typography.Text>
                </div>
              </div>
            </List.Item>
          )}
        />
      </Card>

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 24 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      {selectedRecord && (
        <Card style={{ marginBottom: 24 }}>
          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            Selected Record
          </Typography.Title>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            Path
          </Typography.Text>
          <pre style={{ overflow: "auto", maxHeight: "120px", marginBottom: 16 }}>
            {selectedRecord.path || ""}
          </pre>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            Request Payload
          </Typography.Text>
          <pre style={{ overflow: "auto", maxHeight: "400px" }}>
            {selectedRecord.requestPayload || ""}
          </pre>
        </Card>
      )}

      {result && (
        <Card>
          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            Replay Response
          </Typography.Title>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Click string values to parse escaped new lines.
          </Typography.Text>
          <div style={{ maxHeight: "500px", overflow: "auto" }}>
            <JsonNode value={result} onStringClick={handleStringClick} path="root" />
          </div>
        </Card>
      )}

      <Modal
        open={stringDialogOpen}
        onCancel={() => setStringDialogOpen(false)}
        title={`Parsed String (${selectedStringPath})`}
        footer={[
          <Button key="close" onClick={() => setStringDialogOpen(false)}>
            Close
          </Button>,
        ]}
        width="70%"
      >
        <pre style={{ overflow: "auto", maxHeight: "60vh", whiteSpace: "pre-wrap" }}>
          {parsedStringContent}
        </pre>
      </Modal>
    </div>
  );
};

export default DebugAgent;
